import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TooltipModule } from 'primeng/tooltip';
import { catchError, forkJoin, of } from 'rxjs';
import { Company } from '../../company/company.model';
import { Customer } from '../../customer/customer.model';
import { CrmAccount, CrmClient, CrmClientCommunication } from '../../crm/crm.model';
import { CrmService } from '../../crm/crm.service';
import { Location } from '../../location/location.model';
import { LazyLocationPickerDirective } from '../../shared/location-picker/lazy-location-picker.directive';
import { LogisticsProjectLookupComponent } from '../../logistics-projects/logistics-project-lookup.component';
import { Country } from '../../models/common/country.model';
import { PayloadType } from '../../models/meta-params/payload-type';
import { UOM } from '../../models/meta-params/uom';
import { BaseMasterDataService } from '../../services/base-master-data.service';
import { SessionStorageService } from '../../services/session-storage.service';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { CommercialPartyPickerComponent } from '../../shared/commercial-party-picker/commercial-party-picker.component';
import { CommercialPartyReference } from '../../shared/commercial-party-picker/commercial-party.model';
import { PartyQuickCreateDialogComponent, PartyQuickCreateResult } from '../../shared/party-quick-create/party-quick-create-dialog.component';
import { TransportOrderRelease } from '../transport-planning.model';
import { TransportPlanningService } from '../transport-planning.service';
import { WarehousingItem, WarehousingMasterData } from '../../warehousing/warehousing.model';
import { WarehousingService } from '../../warehousing/warehousing.service';

@Component({
  selector: 'app-transport-release-form',
  standalone: true,
  imports: [
    LogisticsProjectLookupComponent,
    CommonModule,
    FormsModule,
    RouterModule,
    ButtonModule,
    DatePickerModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputTextModule,
    InputNumberModule,
    SelectModule,
    LazyLocationPickerDirective,
    TooltipModule,
    CommercialPartyPickerComponent,
    PartyQuickCreateDialogComponent,
    PageShellComponent,
    PageHeroComponent,
    PageDataPanelComponent
  ],
  providers: [BaseMasterDataService],
  templateUrl: './transport-release-form.component.html',
  styleUrls: ['./transport-release-form.component.css']
})
export class TransportReleaseFormComponent implements OnInit {
  clients: CrmClient[] = [];
  accounts: CrmAccount[] = [];
  contacts: CrmClientCommunication[] = [];
  countries: Country[] = [];
  warehousingItems: WarehousingItem[] = [];
  warehouses: WarehousingMasterData[] = [];
  zones: WarehousingMasterData[] = [];
  conditions: WarehousingMasterData[] = [];
  startLocation?: Location;
  endLocation?: Location;
  isLoading = false;
  message = '';

  pickupDate: Date | null = null;
  deliveryDate: Date | null = null;
  payloadTypeId: number | null = null;
  payloadTypeOptions: { label: string; value: number }[] = [];
  units: number | null = null;
  unitOfMeasure = '';
  splitPolicy: 'NONE' | 'LINE' | 'QUANTITY' = 'NONE';
  minimumSplitQuantity: number | null = null;
  maximumSplits: number | null = null;
  readonly splitPolicyOptions = [
    { label: 'Do not split', value: 'NONE' },
    { label: 'Split by complete lines', value: 'LINE' },
    { label: 'Allow quantity split', value: 'QUANTITY' }
  ];
  selectedInventoryItemId?: number | null = null;
  selectedSourceWarehouseId?: number | null = null;
  selectedSourceZoneId?: number | null = null;
  selectedConditionId?: number | null = null;
  uomOptions: { label: string; value: string }[] = [];
  customerTrackingNumber = '';
  selectedCrmClientId?: number | null = null;
  selectedCrmAccountId?: number | null = null;
  selectedCrmContactId?: number | null = null;
  commercialPartyReference: CommercialPartyReference = { source: 'CRM' };
  selectedLogisticsProjectId?: number | null = null;
  private selectedContactCustomerId?: number | null = null;

  showClientQuickCreateDialog = false;
  showContactQuickCreateDialog = false;
  showSourceLocationQuickCreateDialog = false;
  showDestinationLocationQuickCreateDialog = false;
  // Field initializers run before constructor-injected services are assigned.
  // Company-aware drafts are created when the user opens the dialog.
  clientQuickCreateDraft: Customer = this.emptyCustomerDraft('Customer contact');
  contactQuickCreateDraft: Customer = this.emptyCustomerDraft('Operations contact');
  sourceLocationQuickCreateDraft: Location = new Location();
  destinationLocationQuickCreateDraft: Location = new Location();

  constructor(
    private service: TransportPlanningService,
    private crmService: CrmService,
    private warehousingService: WarehousingService,
    private baseMasterDataService: BaseMasterDataService,
    private session: SessionStorageService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.payloadTypeOptions = this.toPayloadTypeOptions(this.session.getPayloadTypes() ?? []);
    this.uomOptions = this.toUomOptions(this.session.getUOMList() ?? []);
    this.countries = this.session.getCountryList() ?? [];
    const companyId = this.companyId();
    if (companyId) {
      forkJoin({
        clients: this.crmService.getClients(companyId),
        accounts: this.crmService.getAccounts(companyId),
        warehousingItems: this.warehousingService.getItems({ activeStatus: 'ACTIVE' }),
        warehouses: this.warehousingService.getMasterData('WAREHOUSE', { activeStatus: 'ACTIVE' }),
        zones: this.warehousingService.getMasterData('ZONE', { activeStatus: 'ACTIVE' }),
        conditions: this.warehousingService.getMasterData('ITEM_CONDITION', { activeStatus: 'ACTIVE' }),
        tenantUoms: this.warehousingService.getMasterData('TENANT_UOM', { activeStatus: 'ACTIVE' })
          .pipe(catchError(() => of({ data: [] as WarehousingMasterData[] }))),
        baseMasterData: this.baseMasterDataService.getBaseMasterData().pipe(catchError(() => of({ data: { uomList: [] } }))),
        countries: this.baseMasterDataService.getCountries().pipe(catchError(() => of([] as Country[])))
      }).subscribe({
        next: response => {
          this.clients = response.clients ?? [];
          this.accounts = response.accounts ?? [];
          this.warehousingItems = response.warehousingItems.data ?? [];
          this.warehouses = response.warehouses.data ?? [];
          this.zones = response.zones.data ?? [];
          this.conditions = response.conditions.data ?? [];
          this.countries = response.countries.length ? response.countries : this.countries;
          this.uomOptions = this.mergeUomOptions(
            this.uomOptions,
            this.toUomOptions(response.baseMasterData.data?.uomList ?? []),
            this.toTenantUomOptions(response.tenantUoms.data ?? [])
          );
        },
        error: () => {
          this.uomOptions = this.mergeUomOptions(this.uomOptions);
          this.message = 'Could not load saved places and release setup data.';
        }
      });
    }
  }

  saveManual(): void {
    if (!this.startLocation || !this.endLocation) {
      this.message = 'Choose where the truck starts and where it goes.';
      return;
    }
    const party = this.commercialPartyReference;
    if (!party.crmClientId && !party.legacyCompanyId && !party.legacyCustomerId) {
      this.message = 'Choose a CRM client or legacy commercial party before adding this release.';
      return;
    }
    const pickupWindowStart = this.dateFromInput(this.pickupDate);
    const deliveryWindowEnd = this.dateFromInput(this.deliveryDate);
    const payloadType = this.payloadTypeOptions.find(option => option.value === this.payloadTypeId);
    const inventoryItem = this.selectedInventoryItem();
    const unitCount = Number(this.units);
    const uom = this.unitOfMeasure.trim();
    if (!pickupWindowStart || !deliveryWindowEnd) {
      this.message = 'Enter the actual pickup and delivery windows.';
      return;
    }
    if (!inventoryItem && !payloadType) {
      this.message = 'Choose a warehouse inventory item or shipment payload type.';
      return;
    }
    if (inventoryItem && (!this.selectedSourceWarehouseId || !this.selectedConditionId)) {
      this.message = 'Warehouse-backed manual releases require source warehouse and item condition.';
      return;
    }
    if (!Number.isFinite(unitCount) || unitCount <= 0 || !uom) {
      this.message = 'Enter units and unit of measure.';
      return;
    }
    const release: TransportOrderRelease = {
      owningCompanyId: this.companyId(),
      sourceType: 'MANUAL',
      customerTrackingNumber: this.customerTrackingNumber.trim() || undefined,
      commercialPartySource: party.source === 'CRM' ? 'CRM' : 'LEGACY',
      crmClientId: party.crmClientId ?? undefined,
      crmAccountId: party.crmAccountId ?? undefined,
      crmClientContactId: party.crmContactId ?? undefined,
      logisticsProjectId: this.selectedLogisticsProjectId || undefined,
      companyId: party.legacyCompanyId ?? undefined,
      customerId: party.legacyCustomerId ?? undefined,
      originLocationId: this.startLocation.id,
      originName: this.startLocation.name,
      originLatitude: this.startLocation.location?.latitude,
      originLongitude: this.startLocation.location?.longitude,
      destinationLocationId: this.endLocation.id,
      destinationName: this.endLocation.name,
      destinationLatitude: this.endLocation.location?.latitude,
      destinationLongitude: this.endLocation.location?.longitude,
      pickupWindowStart,
      deliveryWindowEnd,
      priority: 'NORMAL',
      splitPolicy: this.splitPolicy,
      minimumSplitQuantity: this.splitPolicy === 'QUANTITY' ? this.minimumSplitQuantity : undefined,
      maximumSplits: this.splitPolicy !== 'NONE' ? this.maximumSplits : undefined,
      lines: [{
        sourceLineType: inventoryItem ? 'WAREHOUSE_ITEM' : 'PAYLOAD_TYPE',
        sourceLineId: String(inventoryItem?.id || payloadType?.value || ''),
        inventoryItemId: inventoryItem?.id,
        itemCode: inventoryItem?.itemNumber || inventoryItem?.skuNumber,
        itemName: inventoryItem?.itemName,
        itemDescription: inventoryItem?.itemDescription || payloadType?.label,
        sourceWarehouseId: inventoryItem ? this.selectedSourceWarehouseId || undefined : undefined,
        sourceZoneId: inventoryItem ? this.selectedSourceZoneId || undefined : undefined,
        conditionId: inventoryItem ? this.selectedConditionId || undefined : undefined,
        quantity: unitCount,
        uom,
        units: unitCount
      }]
    };
    this.isLoading = true;
    this.service.createRelease(release).subscribe({
      next: row => this.done('Release added.', row),
      error: error => {
        this.message = this.apiErrorMessage(error) ?? 'Could not add release.';
        this.isLoading = false;
      }
    });
  }

  onCommercialPartyChange(reference: CommercialPartyReference): void {
    this.commercialPartyReference = reference;
  }

  filteredAccounts(): CrmAccount[] {
    const clientId = this.selectedCrmClientId;
    return this.accounts.filter(account => !clientId || !account.crmClientId || account.crmClientId === clientId);
  }

  onCrmClientChange(): void {
    const account = this.accounts.find(row => row.id === this.selectedCrmAccountId);
    if (account?.crmClientId && account.crmClientId !== this.selectedCrmClientId) {
      this.selectedCrmAccountId = null;
    }
    this.selectedCrmContactId = null;
    this.selectedContactCustomerId = null;
    this.contacts = [];
    if (this.selectedCrmClientId) {
      this.loadClientContacts(this.selectedCrmClientId);
    }
  }

  onCrmContactChange(): void {
    this.selectedContactCustomerId = null;
  }

  openClientQuickCreate(): void {
    this.clientQuickCreateDraft = this.newCustomerDraft('Customer contact');
    this.showClientQuickCreateDialog = true;
  }

  openContactQuickCreate(): void {
    if (!this.selectedCrmClientId) {
      this.message = 'Choose the CRM client before adding a contact.';
      return;
    }
    this.contactQuickCreateDraft = this.newCustomerDraft('Operations contact');
    this.showContactQuickCreateDialog = true;
  }

  openLocationQuickCreate(kind: 'source' | 'destination'): void {
    if (kind === 'source') {
      this.sourceLocationQuickCreateDraft = new Location();
      this.showSourceLocationQuickCreateDialog = true;
      return;
    }
    this.destinationLocationQuickCreateDraft = new Location();
    this.showDestinationLocationQuickCreateDialog = true;
  }

  onClientQuickCreated(result: PartyQuickCreateResult): void {
    const customer = result.record as Customer;
    const companyId = this.companyId();
    if (!customer?.id || !companyId) {
      this.message = 'Customer was created, but its CRM client record could not be prepared.';
      return;
    }
    const client: CrmClient = {
      companyName: customer.company?.name || customer.name || 'New customer',
      customerNameLocal: customer.name || '',
      notifyingPersonName: customer.name || '',
      postingDate: new Date(),
      currencyCode: 'SAR',
      exchangeRate: 1,
      businessType: 'B2B',
      category: 'STANDARD',
      website: '',
      source: 'TRANSPORT_PLANNING',
      kamCustomerId: 0,
      primaryEmail: customer.emailAddress || '',
      phoneNumber: customer.phoneNumber || '',
      mobileNumber: customer.phoneNumber || '',
      countryId: customer.countryId || this.owningCompany()?.countryId,
      linkedCompanyId: customer.company?.id,
      linkedCustomerId: customer.id,
      roles: ['CUSTOMER'],
      companyId
    };
    this.crmService.addClient(client).subscribe({
      next: response => {
        const created = response.data;
        if (!response.success || !created?.id) {
          this.message = response.message || 'The customer was created but the CRM client could not be saved.';
          return;
        }
        this.clients = this.upsertById(this.clients, created);
        this.selectedCrmClientId = created.id;
        this.selectedCrmAccountId = null;
        this.contacts = [];
        this.saveQuickContact(created, customer, true);
      },
      error: error => this.message = this.apiErrorMessage(error) ?? 'The customer was created but the CRM client could not be saved.'
    });
  }

  onContactQuickCreated(result: PartyQuickCreateResult): void {
    const client = this.selectedCrmClient();
    const customer = result.record as Customer;
    if (!client?.id || !customer?.id) {
      this.message = 'Contact was created, but it could not be linked to the CRM client.';
      return;
    }
    this.saveQuickContact(client, customer, false);
  }

  onLocationQuickCreated(kind: 'source' | 'destination', result: PartyQuickCreateResult): void {
    const location = result.record as Location;
    if (!location?.id) {
      this.message = 'Location was created, but no location id was returned.';
      return;
    }
    if (kind === 'source') {
      this.startLocation = location;
      this.showSourceLocationQuickCreateDialog = false;
    } else {
      this.endLocation = location;
      this.showDestinationLocationQuickCreateDialog = false;
    }
    if (!this.hasCoordinates(location)) {
      this.message = 'Location was added. Set its map coordinates before creating the release.';
    }
  }

  onQuickCreateFailed(message: string): void {
    this.message = message || 'The quick-create action could not be completed.';
  }

  clientOptionLabel(client: CrmClient): string {
    return [client.clientNumber, client.companyName || client.customerNameLocal || client.notifyingPersonName]
      .filter(Boolean)
      .join(' - ') || `CRM Client #${client.id}`;
  }

  accountOptionLabel(account: CrmAccount): string {
    return account.companyName || `Account #${account.id}`;
  }

  contactOptionLabel(contact: CrmClientCommunication): string {
    return [contact.name, contact.email || contact.mobile || contact.phone]
      .filter(Boolean)
      .join(' - ') || `Contact #${contact.id}`;
  }

  warehousingItemLabel(item: WarehousingItem): string {
    return [item.itemNumber || item.skuNumber, item.itemName].filter(Boolean).join(' - ') || `Item #${item.id}`;
  }

  zoneOptionsForSelectedWarehouse(): WarehousingMasterData[] {
    return this.zones.filter(zone => !this.selectedSourceWarehouseId || zone.parentId === this.selectedSourceWarehouseId);
  }

  onSourceWarehouseChange(): void {
    if (this.selectedSourceZoneId && !this.zoneOptionsForSelectedWarehouse().some(zone => zone.id === this.selectedSourceZoneId)) {
      this.selectedSourceZoneId = null;
    }
  }

  selectedInventoryItem(): WarehousingItem | undefined {
    return this.warehousingItems.find(item => item.id === this.selectedInventoryItemId);
  }

  private done(message: string, result?: TransportOrderRelease | TransportOrderRelease[]): void {
    this.message = message;
    this.isLoading = false;
    const rows = Array.isArray(result) ? result : (result ? [result] : []);
    const orchestrationRelease = rows.find(row => row.crossDockOrchestrationId);
    if (orchestrationRelease?.crossDockOrchestrationId) {
      this.router.navigate(['/transport-planning/orchestrations', orchestrationRelease.crossDockOrchestrationId]);
      return;
    }
    this.router.navigate(['/transport-planning/releases']);
  }

  private selectedCrmClient(): CrmClient | undefined {
    return this.clients.find(client => client.id === this.selectedCrmClientId);
  }

  private loadClientContacts(clientId: number): void {
    this.crmService.getClientMasterCommunications(clientId, this.companyId()).subscribe({
      next: contacts => this.contacts = contacts ?? [],
      error: () => {
        this.contacts = [];
        this.message = 'Could not load CRM contacts for the selected client.';
      }
    });
  }

  private saveQuickContact(client: CrmClient, customer: Customer, primaryRecord: boolean): void {
    if (!client.id) return;
    const contact: CrmClientCommunication = {
      clientId: client.id,
      companyId: this.companyId(),
      customerId: customer.id,
      communicationType: 'OPERATION',
      name: customer.name || '',
      email: customer.emailAddress || '',
      phone: customer.phoneNumber || '',
      mobile: customer.phoneNumber || '',
      primaryRecord
    };
    this.crmService.saveClientMasterCommunication(client.id, contact, this.companyId()).subscribe({
      next: response => {
        const saved = response.data;
        if (!response.success || !saved?.id) {
          this.message = response.message || 'The CRM client was saved, but its contact could not be linked.';
          return;
        }
        this.contacts = this.upsertById(this.contacts, saved);
        this.selectedCrmContactId = saved.id;
        this.selectedContactCustomerId = customer.id;
        this.showClientQuickCreateDialog = false;
        this.showContactQuickCreateDialog = false;
      },
      error: error => this.message = this.apiErrorMessage(error) ?? 'The contact was created but could not be linked to the CRM client.'
    });
  }

  owningCompany(): Company | null {
    return this.session?.getCompany?.() ?? null;
  }

  private newCustomerDraft(corporateRole: string): Customer {
    const draft = this.emptyCustomerDraft(corporateRole);
    draft.countryId = this.owningCompany()?.countryId || 0;
    return draft;
  }

  private emptyCustomerDraft(corporateRole: string): Customer {
    return {
      name: '',
      emailAddress: '',
      phoneNumber: '',
      corporateRole,
      address: '',
      countryId: 0,
      roles: []
    } as unknown as Customer;
  }

  private hasCoordinates(location: Location): boolean {
    return Number.isFinite(location.location?.latitude) && Number.isFinite(location.location?.longitude);
  }

  private upsertById<T extends { id?: number }>(rows: T[], row: T): T[] {
    const index = rows.findIndex(existing => existing.id === row.id);
    if (index < 0) return [...rows, row];
    return rows.map(existing => existing.id === row.id ? row : existing);
  }

  private apiErrorMessage(error: unknown): string | null {
    if (!error || typeof error !== 'object') {
      return null;
    }
    const response = error as { error?: unknown; message?: unknown };
    if (typeof response.error === 'string' && response.error.trim()) {
      return response.error.trim();
    }
    if (response.error && typeof response.error === 'object') {
      const message = (response.error as { message?: unknown }).message;
      if (typeof message === 'string' && message.trim()) {
        return message.trim();
      }
    }
    return typeof response.message === 'string' && response.message.trim()
      ? response.message.trim()
      : null;
  }

  companyId(): number {
    return this.session.getCompany()?.id ?? 0;
  }

  private dateFromInput(value: Date | string | null): Date | null {
    if (!value) return null;
    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value;
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  private toPayloadTypeOptions(payloadTypes: PayloadType[]): { label: string; value: number }[] {
    return payloadTypes
      .map(payloadType => {
        const value = Number(payloadType.typeId);
        const label = payloadType.name?.trim() ?? '';
        return Number.isFinite(value) && label ? { label, value } : null;
      })
      .filter((option): option is { label: string; value: number } => !!option);
  }

  private toUomOptions(uoms: UOM[]): { label: string; value: string }[] {
    return uoms
      .map(uom => {
        const value = uom.shortName || uom.name || '';
        const label = [uom.name, uom.shortName && uom.shortName !== uom.name ? uom.shortName : null]
          .filter(Boolean)
          .join(' - ');
        return value ? { label: label || value, value } : null;
      })
      .filter((option): option is { label: string; value: string } => !!option);
  }

  private toTenantUomOptions(uoms: WarehousingMasterData[]): { label: string; value: string }[] {
    return uoms
      .map(uom => {
        const value = (uom.code || uom.name || '').trim();
        const label = [uom.name, uom.code && uom.code !== uom.name ? uom.code : null].filter(Boolean).join(' - ');
        return value ? { label: label || value, value } : null;
      })
      .filter((option): option is { label: string; value: string } => !!option);
  }

  private mergeUomOptions(...sources: { label: string; value: string }[][]): { label: string; value: string }[] {
    const byValue = new Map<string, { label: string; value: string }>();
    for (const option of [...sources.flat(), { label: 'Each - EA', value: 'EA' }]) {
      const value = option.value.trim();
      if (value && !byValue.has(value.toUpperCase())) {
        byValue.set(value.toUpperCase(), { ...option, value });
      }
    }
    return Array.from(byValue.values()).sort((a, b) => a.label.localeCompare(b.label));
  }
}
